/**
 * i18n setup for the Portal UI (zh / en).
 *
 * Scope: Portal UI text only. ivt/ and eventHub/ keep their own text, and
 * agent replies are not translated.
 *
 * Behind UI_I18N_ENABLED (config.ts, per stage). Off: the UI renders the
 * `legacy` language, the text it showed before the switch, and nothing else
 * changes. On: the UI starts in Chinese, the user popover offers a switch, and
 * <html lang> follows the locale. Either way a stored choice
 * (`localStorage.rockyLang`, written by the switch) wins, which is also how to
 * try a locale locally.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { UI_I18N_ENABLED } from '../config';
import { applyHtmlLang, isLocale, LOCALE_STORAGE_KEY } from './locale';
import { DEFAULT_NS, LEGACY_LANGUAGE, legacyResources, resources, type Locale } from './resources';

export { LOCALE_STORAGE_KEY } from './locale';

/** The locale for locale-aware data and formatting while the legacy text is shown. */
export const LEGACY_DATA_LOCALE: Locale = 'zh';

/** A UI language: a locale, or the pre-switch legacy text. */
export type UiLanguage = Locale | typeof LEGACY_LANGUAGE;

/** The locale the UI starts in, with the switch on, until the user picks one. */
export const DEFAULT_LOCALE: Locale = 'zh';

/** The language to start with: a valid stored choice, else legacy (switch off) or the default locale (switch on). */
export function resolveInitialLanguage({
  enabled,
  storage,
}: {
  enabled: boolean;
  storage: Pick<Storage, 'getItem'> | undefined;
}): UiLanguage {
  try {
    const stored = storage?.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // Storage can throw (privacy mode, disabled cookies); fall through.
  }
  return enabled ? DEFAULT_LOCALE : LEGACY_LANGUAGE;
}

const initial = resolveInitialLanguage({
  enabled: UI_I18N_ENABLED,
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
});

void i18n.use(initReactI18next).init({
  resources: { ...resources, [LEGACY_LANGUAGE]: legacyResources },
  lng: initial,
  fallbackLng: LEGACY_DATA_LOCALE,
  defaultNS: DEFAULT_NS,
  ns: Object.keys(resources.zh),
  interpolation: { escapeValue: false }, // React already escapes rendered text
});

// With the switch on, <html lang> follows the locale from the first render;
// off, it keeps the value from index.html.
if (UI_I18N_ENABLED && isLocale(initial) && typeof document !== 'undefined') applyHtmlLang(document, initial);

export default i18n;
